import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Play, Search, Calendar, User, FileText, MonitorPlay } from "lucide-react";
import { format } from "date-fns";

interface VideoAssignment {
  id: string;
  video_id: string;
  teacher_id: string;
  assigned_by: string;
  assigned_at: string;
  notes?: string;
  video: {
    id: string;
    title: string;
    description: string;
    video_url: string;
    thumbnail_url?: string;
    duration_seconds?: number;
    category: string;
    tags: string[];
    upload_type: 'admin' | 'teacher';
    uploaded_by: string;
    is_public: boolean;
    status: string;
    created_at: string;
  };
  assigner_name?: string;
}

export default function VideoAssignments() {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [assignments, setAssignments] = useState<VideoAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAssignment, setSelectedAssignment] = useState<VideoAssignment | null>(null);
  const [showVideoDialog, setShowVideoDialog] = useState(false);

  useEffect(() => {
    if (user) {
      loadAssignments();
    }
  }, [user]);

  const loadAssignments = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('video_assignments')
        .select('*, video:videos(*)')
        .eq('teacher_id', user?.id)
        .order('assigned_at', { ascending: false });

      if (error) throw error;

      // Get assigner names
      const assignmentsWithNames = await Promise.all(
        (data || []).map(async (assignment) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('first_name, last_name')
            .eq('id', assignment.assigned_by)
            .single();
          
          return {
            ...assignment,
            video: {
              ...assignment.video,
              upload_type: assignment.video.upload_type as 'admin' | 'teacher'
            },
            assigner_name: profile 
              ? `${profile.first_name} ${profile.last_name}`
              : 'Unknown'
          };
        })
      );

      setAssignments(assignmentsWithNames);
    } catch (error) {
      console.error('Error loading assignments:', error);
      toast({
        title: "Error",
        description: "Failed to load video assignments",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const openVideoPlayer = (assignment: VideoAssignment) => {
    setSelectedAssignment(assignment);
    setShowVideoDialog(true);
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'professional_development': 'bg-blue-100 text-blue-800',
      'instructional_strategies': 'bg-green-100 text-green-800',
      'classroom_management': 'bg-purple-100 text-purple-800',
      'technology': 'bg-orange-100 text-orange-800',
      'assessment': 'bg-red-100 text-red-800',
      'curriculum': 'bg-indigo-100 text-indigo-800'
    };
    return colors[category.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const filteredAssignments = assignments.filter(assignment =>
    assignment.video.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    assignment.video.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    assignment.assigner_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Video Assignments</h1>
          <p className="text-muted-foreground">Videos assigned to you by administrators</p>
        </div>
        <Badge variant="outline" className="text-lg px-4 py-2">
          {assignments.length} Assignment{assignments.length !== 1 ? 's' : ''}
        </Badge>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search assignments..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Assignments Grid */}
      {filteredAssignments.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <MonitorPlay className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Video Assignments</h3>
            <p className="text-muted-foreground">
              {searchTerm ? 'No assignments match your search.' : 'You have no video assignments at this time.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredAssignments.map((assignment) => (
            <Card key={assignment.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg leading-tight">{assignment.video.title}</CardTitle>
                    <CardDescription className="line-clamp-2 mt-1">
                      {assignment.video.description}
                    </CardDescription>
                  </div>
                  <Badge className={getCategoryColor(assignment.video.category)}>
                    {assignment.video.category.replace('_', ' ')}
                  </Badge>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Assigned by: {assignment.assigner_name}
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {format(new Date(assignment.assigned_at), 'MMM d, yyyy')}
                  </div>
                  {assignment.video.duration_seconds && (
                    <div className="flex items-center gap-2">
                      <Play className="h-4 w-4" />
                      {Math.ceil(assignment.video.duration_seconds / 60)} minutes
                    </div>
                  )}
                </div>

                {assignment.notes && (
                  <div className="bg-muted p-3 rounded-lg">
                    <div className="flex items-center gap-2 text-sm font-medium mb-1">
                      <FileText className="h-4 w-4" />
                      Assignment Notes:
                    </div>
                    <p className="text-sm text-muted-foreground">{assignment.notes}</p>
                  </div>
                )}

                {assignment.video.tags && assignment.video.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {assignment.video.tags.map((tag, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}

                <Button 
                  onClick={() => openVideoPlayer(assignment)}
                  className="w-full"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Watch Video
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Video Player Dialog */}
      <Dialog open={showVideoDialog} onOpenChange={setShowVideoDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{selectedAssignment?.video.title}</DialogTitle>
          </DialogHeader>
          {selectedAssignment && (
            <div className="space-y-4">
              <div className="aspect-video bg-black rounded-lg overflow-hidden">
                <video
                  src={selectedAssignment.video.video_url}
                  controls
                  className="w-full h-full"
                  poster={selectedAssignment.video.thumbnail_url}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  {selectedAssignment.video.description}
                </p>
                {selectedAssignment.notes && (
                  <div className="bg-muted p-3 rounded-lg">
                    <p className="text-sm font-medium mb-1">Assignment Notes:</p>
                    <p className="text-sm">{selectedAssignment.notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}